/**
 * Renderer de widgets HTML (externalUrl ou assets do tipo widget).
 * Usa iframe sandboxed — sem acesso ao DOM pai, sem cookies, sem storage.
 *
 * `allow-same-origin` é deliberadamente omitido: combinado com `allow-scripts`
 * permite que o conteúdo carregado (HTML de asset enviado por anunciante, ou
 * página externa) escape do sandbox e manipule o mesmo contexto de origem do
 * player. `allow-popups` também é omitido — o player roda sem interação do
 * usuário (tela não assistida), então não há cenário legítimo de popup.
 */
import { useEffect, useRef } from 'react'
import type { PlaybackResult } from '@/types/player.types'

interface Props {
  url: string
  durationSeconds: number
  /**
   * Sempre `played: true` com o tempo medido em tela: um iframe sandboxed cross-origin não expõe
   * sinal confiável de falha de carregamento (página de erro também dispara `load`).
   */
  onEnd: (result: PlaybackResult) => void
}

export function WebViewRenderer({ url, durationSeconds, onEnd }: Props) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const endSentRef = useRef(false)
  const onEndRef = useRef(onEnd)
  onEndRef.current = onEnd

  useEffect(() => {
    endSentRef.current = false
    const startedAt = Date.now()
    timerRef.current = setTimeout(() => {
      if (endSentRef.current) return
      endSentRef.current = true
      onEndRef.current({ played: true, actualSeconds: (Date.now() - startedAt) / 1000, startedAt })
    }, durationSeconds * 1000)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [durationSeconds])

  return (
    <iframe
      src={url}
      sandbox="allow-scripts allow-presentation"
      allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
      referrerPolicy="no-referrer"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        border: 'none',
        background: '#000'
      }}
      title="widget"
      scrolling="no"
    />
  )
}
